import React, { useEffect, useMemo, useState } from 'react'

import { supabase } from '../../lib/supabase'
import { toast } from 'sonner'
import { Button, Input, Card, Badge } from '../../components/UI'
import { Plus, Lock, Unlock, Copy, Trash2, ChevronDown, ChevronUp, Settings, Edit, Users, Shield, Zap, Calendar, Clock, Beaker, Activity, Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react'

export default function LicensesPage() {
    const [licenses, setLicenses] = useState([])
    const [projects, setProjects] = useState([])
    const [loading, setLoading] = useState(true)

    // Modals state
    const [isCreateOpen, setIsCreateOpen] = useState(false)
    const [editingLicense, setEditingLicense] = useState(null)
    const [extendingLicense, setExtendingLicense] = useState(null) // Nuevo modal extender

    const [expandedId, setExpandedId] = useState(null)

    // Form Data Create
    const [createForm, setCreateForm] = useState({ projectName: '', clientName: '', domains: '', maxUsers: 5, days: 30 })

    // Form Data Edit
    const [editForm, setEditForm] = useState({ features: {}, maxUsers: 0, clientName: '', domains: '', expiresAt: '' })

    // Soft-warning revocación (Regla 15)
    const [confirmRevokeId, setConfirmRevokeId] = useState(null)

    // Filtros y Paginación
    const [searchQuery, setSearchQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState('all') // 'all' | 'active' | 'revoked' | 'expired'
    const [currentPage, setCurrentPage] = useState(1)
    const PAGE_SIZE = 10

    // Licencias filtradas
    const filteredLicenses = useMemo(() => {
        let result = licenses

        // Búsqueda por nombre de cliente o proyecto
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase()
            result = result.filter((l: any) =>
                (l.client_name || '').toLowerCase().includes(q) ||
                (l.projects?.name || '').toLowerCase().includes(q) ||
                (l.client_id || '').toLowerCase().includes(q)
            )
        }

        // Filtro por estado
        if (statusFilter === 'active') result = result.filter((l: any) => l.status === 'active')
        else if (statusFilter === 'revoked') result = result.filter((l: any) => l.status === 'revoked')
        else if (statusFilter === 'expired') result = result.filter((l: any) => {
            if (!l.expires_at) return false
            return new Date(l.expires_at) < new Date()
        })

        return result
    }, [licenses, searchQuery, statusFilter])

    // Paginación
    const totalPages = Math.max(1, Math.ceil(filteredLicenses.length / PAGE_SIZE))
    const paginatedLicenses = filteredLicenses.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

    // Reset page al cambiar filtros
    useEffect(() => { setCurrentPage(1) }, [searchQuery, statusFilter])

    // KPIs
    const kpiCounts = useMemo(() => {
        const total = licenses.length
        const active = licenses.filter((l: any) => l.status === 'active').length
        const revoked = licenses.filter((l: any) => l.status === 'revoked').length
        const expired = licenses.filter((l: any) => l.expires_at && new Date(l.expires_at) < new Date()).length
        return { total, active, revoked, expired }
    }, [licenses])

    useEffect(() => { fetchData() }, [])

    const fetchData = async () => {
        setLoading(true)
        try {
            // 1. Obtener Licencias Base
            const { data: lic, error } = await supabase
                .from('licenses')
                .select('*, projects(name)')
                .order('created_at', { ascending: false })

            if (error) throw error

            // 2. Obtener Actividad usando RPC de Diagnóstico (que sabemos que funciona)
            // Hacemos una llamada por licencia (paralelas)
            const activityPromises = lic.map(async (l) => {
                // Usamos el RPC de diagnóstico porque es el único que el usuario ha confirmado que funciona
                const { data, error: rpcError } = await supabase.rpc('ai_diagnose_license', { p_license_id: l.id })

                if (rpcError) {
                    console.warn('[RPC Error]', l.client_name, rpcError)
                    return { id: l.id, count: 0 }
                }

                return { id: l.id, count: data?.active_users_5min || 0 }
            })

            const activities = await Promise.all(activityPromises)
            const activityMap = activities.reduce((acc, curr) => ({ ...acc, [curr.id]: curr.count }), {})

            // 3. Fusionar datos
            const merged = lic.map(l => ({
                ...l,
                active_users: activityMap[l.id] !== undefined ? activityMap[l.id] : 0, // Fallback explícito
                active_sessions: activityMap[l.id] || 0,
                last_activity: l.last_heartbeat // Fallback a heartbeat general
            }))

            setLicenses(merged)
        } catch (e) {
            toast.error('Error cargando licencias')
            console.error(e)
        }

        const { data: proj } = await supabase.from('projects').select('*')
        setProjects(proj || [])
        setLoading(false)
    }

    // --- ACCIONES ---

    const revokeLicense = async (id, name) => {
        // Soft-warning en vez de confirm() nativo (Regla 15)
        if (confirmRevokeId !== id) {
            setConfirmRevokeId(id)
            toast('⚠️ Pulsa de nuevo para confirmar revocación de ' + name, { duration: 3000 })
            setTimeout(() => setConfirmRevokeId(null), 3000)
            return
        }
        setConfirmRevokeId(null)
        try {
            const { error } = await supabase.rpc('ai_revoke_license', { p_target: id, p_reason: 'Admin UI' })
            if (error) throw error
            toast.success(`Licencia revocada: ${name}`)
            fetchData()
        } catch (e) { toast.error(e.message) }
    }

    const restoreLicense = async (id) => {
        const { error } = await supabase.from('licenses').update({ status: 'active' }).eq('id', id)
        if (error) toast.error('Error al restaurar')
        else { toast.success('Licencia restaurada'); fetchData() }
    }

    const handleCreate = async (e) => {
        e.preventDefault()
        setLoading(true)
        try {
            // Resolver Project ID
            let projectId
            const slug = createForm.projectName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'proj-' + Date.now()

            const { data: existing } = await supabase.from('projects').select('id').eq('name', createForm.projectName).single()
            if (existing) projectId = existing.id
            else {
                const { data: newP, error } = await supabase.from('projects').insert({ name: createForm.projectName, slug }).select().single()
                if (error) throw error
                projectId = newP.id
            }

            // Calcular expiración
            const expiresAt = new Date()
            expiresAt.setDate(expiresAt.getDate() + parseInt(createForm.days))

            // Crear Licencia
            const { error } = await supabase.from('licenses').insert({
                project_id: projectId,
                client_name: createForm.clientName,
                client_id: self.crypto.randomUUID(),
                allowed_domains: createForm.domains.split(',').map(d => d.trim()),
                status: 'active',
                quotas: { max_users: parseInt(createForm.maxUsers) || 5 },
                features: { finance: true, reports: true },
                expires_at: expiresAt.toISOString()
            })
            if (error) throw error

            toast.success('Licencia generada')
            setIsCreateOpen(false)
            setCreateForm({ projectName: '', clientName: '', domains: '', maxUsers: 5, days: 30 })
            fetchData()
        } catch (e) { toast.error(e.message) }
        finally { setLoading(false) }
    }

    const openEdit = (lic) => {
        setEditingLicense(lic)
        const domainsStr = Array.isArray(lic.allowed_domains) ? lic.allowed_domains.join(', ') : (lic.allowed_domains || '')
        setEditForm({
            features: lic.features || { finance: false, reports: false },
            maxUsers: lic.max_users || lic.quotas?.max_users || 5,
            clientName: lic.client_name || '',
            domains: domainsStr,
            expiresAt: lic.expires_at ? new Date(lic.expires_at).toISOString().split('T')[0] : ''
        })
    }

    const handleUpdate = async (e) => {
        e.preventDefault()
        try {
            // Parsear dominios a array
            const domainsArray = editForm.domains
                ? editForm.domains.split(',').map(d => d.trim()).filter(Boolean)
                : null

            // Parsear fecha
            const expiresAt = editForm.expiresAt
                ? new Date(editForm.expiresAt + 'T23:59:59Z').toISOString()
                : null

            const { data, error } = await supabase.rpc('tower_update_license', {
                p_license_id: editingLicense.id,
                p_features: editForm.features,
                p_quotas: { max_users: parseInt(editForm.maxUsers) },
                p_client_name: editForm.clientName || null,
                p_allowed_domains: domainsArray,
                p_expires_at: expiresAt
            })

            if (error) throw error

            toast.success('Licencia actualizada correctamente')
            setEditingLicense(null)
            fetchData()
        } catch (e) {
            console.error(e)
            toast.error('Error actualizando: ' + e.message)
        }
    }

    const handleExtend = async (days) => {
        try {
            const { error } = await supabase.rpc('ai_extend_license', {
                p_client_id: extendingLicense.client_id,
                p_days: days
            })
            if (error) throw error
            toast.success(`Licencia extendida ${days} días`)
            setExtendingLicense(null)
            fetchData()
        } catch (e) { toast.error('Error extendiendo: ' + e.message) }
    }

    const toggleFeature = (key) => {
        setEditForm(prev => ({
            ...prev,
            features: { ...prev.features, [key]: !prev.features[key] }
        }))
    }

    const [confirmSimId, setConfirmSimId] = useState(null)
    const handleSimulateTraffic = async (lic) => {
        // Soft-warning en vez de confirm() nativo (Regla 15)
        if (confirmSimId !== lic.id) {
            setConfirmSimId(lic.id)
            toast('⚠️ Pulsa de nuevo para confirmar simulación', { duration: 3000 })
            setTimeout(() => setConfirmSimId(null), 3000)
            return
        }
        setConfirmSimId(null)
        try {
            // Usar RPC seguro para saltar RLS
            const { data, error } = await supabase.rpc('ai_simulate_traffic', { p_license_id: lic.id })

            if (error) throw error
            if (!data.success) throw new Error(data.error || 'Error desconocido')

            toast.success('Tráfico simulado enviado 🚀')

            // Esperar un poco para que el backend procese
            setTimeout(fetchData, 1000)
        } catch (e) {
            console.error(e)
            toast.error('Error simulando: ' + e.message)
            // Fallback: Intentar inserción directa si RPC falla (por si acaso no se ha creado aún en BD)
            try {
                const sid = 'fallback-user-' + Date.now()
                await supabase.from('telemetry_events').insert({
                    license_id: lic.id,
                    event_type: 'session_start',
                    payload: { sid, url: '/fallback', ua: 'FallbackBot' }
                })
            } catch (e2) { console.error('Fallback failed', e2) }
        }
    }

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text)
        toast.success('Copiado')
    }

    const handleDiagnose = async (lic) => {
        const toastId = toast.loading('Ejecutando diagnóstico...')
        try {
            const { data, error } = await supabase.rpc('ai_diagnose_license', { p_license_id: lic.id })

            if (error) throw error

            console.log('[Diagnosis]', data)
            toast.dismiss(toastId)

            // ACTUALIZAR ESTADO LOCAL (Por si el automatico falla)
            if (data.active_users_5min !== undefined) {
                setLicenses(prev => prev.map(pl =>
                    pl.id === lic.id
                        ? { ...pl, active_users: data.active_users_5min, active_sessions: data.active_users_5min }
                        : pl
                ))
            }

            const msg = `
                📊 DIAGNÓSTICO: ${lic.client_name}
                --------------------------------
                🕒 Hora Servidor: ${new Date(data.server_time).toLocaleTimeString()}
                🚦 Estado: ${data.status} ${data.is_expired ? '(EXPIRADA)' : '(VIGENTE)'}
                📈 Eventos Totales en DB: ${data.total_events_db}
                ⏱️ Último Evento: ${data.last_event_db ? new Date(data.last_event_db).toLocaleString() : 'NUNCA'}
                👥 Usuarios Activos (5min): ${data.active_users_5min}
            `.replace(/^ +/gm, '')

            alert(msg) // Usamos alert para que el usuario pueda copiar/verlo bien

        } catch (e) {
            toast.dismiss(toastId)
            toast.error('Error diagnóstico: ' + e.message)
            if (e.message?.includes('not found')) toast.error('Falta ejecutar db_diagnose.sql')
        }
    }

    return (
        <div className="p-6 max-w-7xl mx-auto pb-20">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-1">Gestión de Licencias</h1>
                    <p className="text-gray-500 text-sm">Control de acceso, expiración y features</p>
                </div>
                <Button onClick={() => setIsCreateOpen(true)} className="bg-neon-blue hover:bg-neon-blue/80 text-black">
                    <Plus size={16} /> Nueva Licencia
                </Button>
            </div>

            {/* KPI Mini Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <button onClick={() => setStatusFilter('all')} className={`p-3 rounded-xl border text-center transition-all ${statusFilter === 'all' ? 'border-neon-blue/40 bg-neon-blue/5' : 'border-white/5 bg-white/[0.02] hover:bg-white/[0.04]'}`}>
                    <div className="text-xl font-bold text-white">{kpiCounts.total}</div>
                    <div className="text-[10px] text-gray-500 uppercase">Total</div>
                </button>
                <button onClick={() => setStatusFilter('active')} className={`p-3 rounded-xl border text-center transition-all ${statusFilter === 'active' ? 'border-green-500/40 bg-green-500/5' : 'border-white/5 bg-white/[0.02] hover:bg-white/[0.04]'}`}>
                    <div className="text-xl font-bold text-green-400">{kpiCounts.active}</div>
                    <div className="text-[10px] text-gray-500 uppercase">Activas</div>
                </button>
                <button onClick={() => setStatusFilter('revoked')} className={`p-3 rounded-xl border text-center transition-all ${statusFilter === 'revoked' ? 'border-red-500/40 bg-red-500/5' : 'border-white/5 bg-white/[0.02] hover:bg-white/[0.04]'}`}>
                    <div className="text-xl font-bold text-red-400">{kpiCounts.revoked}</div>
                    <div className="text-[10px] text-gray-500 uppercase">Revocadas</div>
                </button>
                <button onClick={() => setStatusFilter('expired')} className={`p-3 rounded-xl border text-center transition-all ${statusFilter === 'expired' ? 'border-yellow-500/40 bg-yellow-500/5' : 'border-white/5 bg-white/[0.02] hover:bg-white/[0.04]'}`}>
                    <div className="text-xl font-bold text-yellow-400">{kpiCounts.expired}</div>
                    <div className="text-[10px] text-gray-500 uppercase">Expiradas</div>
                </button>
            </div>

            {/* Barra de Búsqueda */}
            <div className="flex items-center gap-3 mb-6">
                <div className="flex-1 relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                        type="text"
                        placeholder="Buscar por cliente, proyecto o ID..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="w-full bg-white/[0.03] border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-gray-600 outline-none focus:border-neon-blue/50 transition-colors"
                    />
                </div>
                {(searchQuery || statusFilter !== 'all') && (
                    <button
                        onClick={() => { setSearchQuery(''); setStatusFilter('all') }}
                        className="text-xs text-gray-400 hover:text-white px-3 py-2.5 bg-white/5 rounded-lg border border-white/10 transition-colors whitespace-nowrap"
                    >
                        Limpiar filtros
                    </button>
                )}
            </div>

            {/* Tabla Principal / Grid Móvil */}
            <div className="grid gap-4 lg:gap-0">
                {/* Vista Desktop (Tabla) - Oculta en pantallas pequeñas/medianas */}
                <Card className="hidden lg:block p-0 overflow-hidden border border-white/5">
                    <table className="w-full text-left">
                        <thead className="bg-white/5 text-gray-400 text-xs uppercase tracking-wider">
                            <tr>
                                <th className="px-6 py-4">Cliente / ID</th>
                                <th className="px-6 py-4">Proyecto</th>
                                <th className="px-6 py-4 text-center">Usuarios</th>
                                <th className="px-6 py-4">Expiración</th>
                                <th className="px-6 py-4">Estado</th>
                                <th className="px-6 py-4 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {paginatedLicenses.map((lic: any) => (
                                <LicenseRow
                                    key={lic.id}
                                    lic={lic}
                                    expandedId={expandedId}
                                    setExpandedId={setExpandedId}
                                    copyToClipboard={copyToClipboard}
                                    setExtendingLicense={setExtendingLicense}
                                    openEdit={openEdit}
                                    revokeLicense={revokeLicense}
                                    restoreLicense={restoreLicense}
                                    handleSimulateTraffic={handleSimulateTraffic}
                                    handleDiagnose={handleDiagnose}
                                />
                            ))}
                            {paginatedLicenses.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500 text-sm">
                                        {searchQuery || statusFilter !== 'all' ? 'Sin resultados para los filtros aplicados' : 'No hay licencias'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </Card>

                {/* Vista Móvil (Tarjetas) - Visible en móvil y tablet */}
                <div className="lg:hidden space-y-4">
                    {paginatedLicenses.map((lic: any) => (
                        <MobileLicenseCard
                            key={lic.id}
                            lic={lic}
                            copyToClipboard={copyToClipboard}
                            setExtendingLicense={setExtendingLicense}
                            openEdit={openEdit}
                            expandedId={expandedId}
                            setExpandedId={setExpandedId}
                            revokeLicense={revokeLicense}
                            restoreLicense={restoreLicense}
                            handleSimulateTraffic={handleSimulateTraffic}
                            handleDiagnose={handleDiagnose}
                        />
                    ))}
                    {paginatedLicenses.length === 0 && (
                        <p className="text-center text-gray-500 text-sm py-12">
                            {searchQuery || statusFilter !== 'all' ? 'Sin resultados para los filtros aplicados' : 'No hay licencias'}
                        </p>
                    )}
                </div>
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                    <span className="text-xs text-gray-500">
                        Mostrando {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, filteredLicenses.length)} de {filteredLicenses.length}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="p-2 rounded-lg bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                            <button
                                key={p}
                                onClick={() => setCurrentPage(p)}
                                className={`w-8 h-8 rounded-lg text-xs font-mono transition-colors ${p === currentPage ? 'bg-neon-blue/20 text-neon-blue border border-neon-blue/30' : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'}`}
                            >
                                {p}
                            </button>
                        ))}
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="p-2 rounded-lg bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}

            {/* Modal CREAR */}
            {isCreateOpen && (
                <ModalOverlay title="Nueva Licencia" onClose={() => setIsCreateOpen(false)}>
                    <form onSubmit={handleCreate} className="space-y-4">
                        <Input label="Proyecto" placeholder="Ej: Gestión Prácticas" value={createForm.projectName} onChange={e => setCreateForm({ ...createForm, projectName: e.target.value })} required />
                        <Input label="Cliente" placeholder="Ej: Autoescuela El Pilar" value={createForm.clientName} onChange={e => setCreateForm({ ...createForm, clientName: e.target.value })} required />

                        <div className="grid grid-cols-2 gap-4">
                            <Input label="Dominios" placeholder="localhost" value={createForm.domains} onChange={e => setCreateForm({ ...createForm, domains: e.target.value })} required />
                            <Input label="Días Validez" type="number" value={createForm.days} onChange={e => setCreateForm({ ...createForm, days: e.target.value })} required />
                        </div>

                        <div className="pt-2 border-t border-white/10">
                            <label className="block text-gray-400 text-xs uppercase mb-2">Límite de Usuarios</label>
                            <input
                                type="number"
                                min="1"
                                className="w-full bg-charcoal border border-white/10 rounded px-3 py-2 text-white outline-none focus:border-neon-blue"
                                value={createForm.maxUsers}
                                onChange={e => setCreateForm({ ...createForm, maxUsers: e.target.value })}
                            />
                        </div>

                        <div className="pt-4 flex gap-3">
                            <Button type="button" variant="ghost" className="flex-1" onClick={() => setIsCreateOpen(false)}>Cancelar</Button>
                            <Button type="submit" className="flex-1" disabled={loading}>Crear Licencia</Button>
                        </div>
                    </form>
                </ModalOverlay>
            )}

            {/* Modal EDITAR */}
            {editingLicense && (
                <ModalOverlay title={`Editar: ${editingLicense.client_name}`} onClose={() => setEditingLicense(null)}>
                    <form onSubmit={handleUpdate} className="space-y-6">
                        {/* Datos del Cliente */}
                        <div>
                            <h3 className="text-sm font-bold text-white mb-3">Datos del Cliente</h3>
                            <div className="bg-white/5 p-4 rounded-lg space-y-3">
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Nombre del Cliente</label>
                                    <input
                                        type="text"
                                        className="w-full bg-charcoal border border-white/10 rounded px-3 py-2 text-white focus:border-neon-blue outline-none"
                                        value={editForm.clientName}
                                        onChange={e => setEditForm({ ...editForm, clientName: e.target.value })}
                                        aria-label="Nombre del cliente"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Dominios Permitidos <span className="text-gray-600">(separados por coma)</span></label>
                                    <input
                                        type="text"
                                        className="w-full bg-charcoal border border-white/10 rounded px-3 py-2 text-white focus:border-neon-blue outline-none font-mono text-sm"
                                        value={editForm.domains}
                                        onChange={e => setEditForm({ ...editForm, domains: e.target.value })}
                                        placeholder="localhost, app.ejemplo.com"
                                        aria-label="Dominios permitidos"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Fecha de Expiración <span className="text-gray-600">(vacío = sin límite)</span></label>
                                    <input
                                        type="date"
                                        className="w-full bg-charcoal border border-white/10 rounded px-3 py-2 text-white focus:border-neon-blue outline-none"
                                        value={editForm.expiresAt}
                                        onChange={e => setEditForm({ ...editForm, expiresAt: e.target.value })}
                                        aria-label="Fecha de expiración"
                                    />
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-sm font-bold text-white mb-3">Concurrencia</h3>
                            <div className="bg-white/5 p-4 rounded-lg flex items-center justify-between">
                                <label className="text-xs text-gray-400">Max Users simultáneos</label>
                                <input
                                    type="number"
                                    className="w-24 bg-charcoal border border-white/10 rounded px-3 py-2 text-white text-right focus:border-neon-blue outline-none"
                                    value={editForm.maxUsers}
                                    onChange={e => setEditForm({ ...editForm, maxUsers: e.target.value })}
                                    aria-label="Máximo de usuarios simultáneos"
                                />
                            </div>
                        </div>

                        <div>
                            <h3 className="text-sm font-bold text-white mb-3">Módulos del Sistema</h3>
                            <div className="bg-white/5 p-4 rounded-lg space-y-3 grid grid-cols-1 md:grid-cols-2 gap-4 gap-y-3">
                                <FeatureToggle label="📅 Agenda / Calendario" active={editForm.features.scheduler} onClick={() => toggleFeature('scheduler')} />
                                <FeatureToggle label="🎓 Alumnos" active={editForm.features.students} onClick={() => toggleFeature('students')} />
                                <FeatureToggle label="👨‍🏫 Profesores" active={editForm.features.professors} onClick={() => toggleFeature('professors')} />
                                <FeatureToggle label="🚗 Vehículos" active={editForm.features.vehicles} onClick={() => toggleFeature('vehicles')} />
                                <FeatureToggle label="💰 Financiero" active={editForm.features.finance} onClick={() => toggleFeature('finance')} />
                                <FeatureToggle label="⚙️ Configuración" active={editForm.features.configuration} onClick={() => toggleFeature('configuration')} />
                                <FeatureToggle label="📈 Reportes Avanzados" active={editForm.features.reports} onClick={() => toggleFeature('reports')} />
                                <FeatureToggle label="🌟 Soporte Premium" active={editForm.features.premium_support} onClick={() => toggleFeature('premium_support')} />
                            </div>

                            <h3 className="text-sm font-bold text-white mt-6 mb-3">Intranet: Módulos Usuario</h3>
                            <div className="bg-white/5 p-4 rounded-lg space-y-3 grid grid-cols-1 md:grid-cols-2 gap-4 gap-y-3">
                                <FeatureToggle label="🔔 Notificaciones" active={editForm.features.notificaciones} onClick={() => toggleFeature('notificaciones')} />
                                <FeatureToggle label="📨 Mensajería (Buzón)" active={editForm.features.buzon} onClick={() => toggleFeature('buzon')} />
                                <FeatureToggle label="📰 Noticias" active={editForm.features.noticias} onClick={() => toggleFeature('noticias')} />
                                <FeatureToggle label="📞 Directorio" active={editForm.features.directorio} onClick={() => toggleFeature('directorio')} />
                                <FeatureToggle label="📋 Permisos" active={editForm.features.permisos} onClick={() => toggleFeature('permisos')} />
                                <FeatureToggle label="📚 Cursos" active={editForm.features.cursos} onClick={() => toggleFeature('cursos')} />
                                <FeatureToggle label="🚗 Vehículos" active={editForm.features.vehiculos} onClick={() => toggleFeature('vehiculos')} />
                                <FeatureToggle label="📅 Fechas CAPA" active={editForm.features.capa} onClick={() => toggleFeature('capa')} />
                                <FeatureToggle label="📝 Plantillas" active={editForm.features.plantillas} onClick={() => toggleFeature('plantillas')} />
                                <FeatureToggle label="🏢 Info Empresa" active={editForm.features.empresa} onClick={() => toggleFeature('empresa')} />
                                <FeatureToggle label="👥 RRHH" active={editForm.features.rrhh} onClick={() => toggleFeature('rrhh')} />
                                <FeatureToggle label="🔗 Enlaces Sistemas" active={editForm.features.sistemas} onClick={() => toggleFeature('sistemas')} />
                                <FeatureToggle label="⚙️ Procesos" active={editForm.features.procesos} onClick={() => toggleFeature('procesos')} />
                                <FeatureToggle label="📁 Documentación" active={editForm.features.docs} onClick={() => toggleFeature('docs')} />
                                <FeatureToggle label="❓ F.A.Q." active={editForm.features.faq} onClick={() => toggleFeature('faq')} />
                            </div>

                            <h3 className="text-sm font-bold text-white mt-6 mb-3">Intranet: Módulos Admin</h3>
                            <div className="bg-white/5 p-4 rounded-lg space-y-3 grid grid-cols-1 md:grid-cols-2 gap-4 gap-y-3">
                                <FeatureToggle label="📢 Admin Avisos" active={editForm.features.admin_hub} onClick={() => toggleFeature('admin_hub')} />
                                <FeatureToggle label="📅 Admin Formación" active={editForm.features.admin_formacion} onClick={() => toggleFeature('admin_formacion')} />
                                <FeatureToggle label="📦 Admin Productos" active={editForm.features.admin_productos} onClick={() => toggleFeature('admin_productos')} />
                                <FeatureToggle label="🏢 Admin Empresa" active={editForm.features.admin_empresa} onClick={() => toggleFeature('admin_empresa')} />
                                <FeatureToggle label="👥 Admin Personas" active={editForm.features.admin_personas} onClick={() => toggleFeature('admin_personas')} />
                                <FeatureToggle label="📝 Admin Plantillas" active={editForm.features.admin_plantillas} onClick={() => toggleFeature('admin_plantillas')} />
                                <FeatureToggle label="⚙️ Admin Sistema" active={editForm.features.admin_sistema} onClick={() => toggleFeature('admin_sistema')} />
                                <FeatureToggle label="💡 Admin Feedback" active={editForm.features.admin_feedback} onClick={() => toggleFeature('admin_feedback')} />
                                <FeatureToggle label="📊 Admin Estadísticas" active={editForm.features.admin_estadisticas} onClick={() => toggleFeature('admin_estadisticas')} />
                            </div>

                            <h3 className="text-sm font-bold text-white mt-6 mb-3">Control de Emergencia</h3>
                            <div className="bg-red-500/5 border border-red-500/20 p-4 rounded-lg">
                                <FeatureToggle label="🔒 BLOQUEO DE MANTENIMIENTO" active={editForm.features.maintenance_mode} onClick={() => toggleFeature('maintenance_mode')} danger />
                                <p className="text-[10px] text-red-300 mt-2 px-1">
                                    * Activa un bloqueo a nivel de servidor. El cliente deberá recargar la página (F5) para ver cambios en este estado.
                                    Para bloqueos temporales rápidos, usa la sección "Comandos".
                                </p>
                            </div>
                        </div>

                        <div className="pt-4 flex gap-3">
                            <Button type="button" variant="ghost" className="flex-1" onClick={() => setEditingLicense(null)}>Cancelar</Button>
                            <Button type="submit" className="flex-1 bg-neon-blue hover:bg-neon-blue/80 text-black font-bold">Guardar</Button>
                        </div>
                    </form>
                </ModalOverlay>
            )}

            {/* Modal EXTENDER */}
            {extendingLicense && (
                <ModalOverlay title={`Extender Validez: ${extendingLicense.client_name}`} onClose={() => setExtendingLicense(null)}>
                    <div className="space-y-4">
                        <p className="text-sm text-gray-400">Selecciona el periodo para extender la licencia. La nueva fecha se calculará desde hoy o desde la fecha actual de expiración (lo que sea mayor).</p>
                        <div className="grid grid-cols-2 gap-3">
                            <Button variant="secondary" onClick={() => handleExtend(7)}>+7 Días</Button>
                            <Button variant="secondary" onClick={() => handleExtend(30)}>+30 Días</Button>
                            <Button variant="secondary" onClick={() => handleExtend(90)}>+3 Meses</Button>
                            <Button variant="secondary" onClick={() => handleExtend(365)}>+1 Año</Button>
                        </div>
                        <Button variant="ghost" className="w-full mt-4" onClick={() => setExtendingLicense(null)}>Cancelar</Button>
                    </div>
                </ModalOverlay>
            )}
        </div>
    )
}


function LicenseRow({ lic, expandedId, setExpandedId, copyToClipboard, setExtendingLicense, openEdit, revokeLicense, restoreLicense, handleSimulateTraffic, handleDiagnose }) {
    const maxUsers = lic.max_users || lic.quotas?.max_users || 0
    const activeUsers = lic.active_users || 0
    const percent = maxUsers > 0 ? (activeUsers / maxUsers) * 100 : 0
    const daysLeft = lic.expires_at ? Math.ceil((new Date(lic.expires_at) - new Date()) / (1000 * 60 * 60 * 24)) : 0

    return (
        <React.Fragment>
            <tr className="hover:bg-white/5 transition-colors group">
                <td className="px-6 py-4">
                    <div className="font-bold text-white mb-1">{lic.client_name}</div>
                    <button onClick={() => copyToClipboard(lic.client_id)} className="text-[10px] text-gray-500 font-mono hover:text-neon-blue flex items-center gap-1 transition-colors">
                        <Shield size={10} />
                        {lic.client_id?.substring(0, 8)}...
                        <Copy size={10} className="opacity-0 group-hover:opacity-100" />
                    </button>
                </td>
                <td className="px-6 py-4 text-sm text-gray-300">
                    {lic.project_name || lic.project_rel_name || lic.projects?.name}
                </td>
                <td className="px-6 py-4">
                    <div className="flex flex-col items-center gap-1">
                        <div className="text-xs font-mono text-white">
                            <span className={activeUsers > maxUsers ? 'text-red-400' : 'text-neon-green'}>{activeUsers}</span>
                            <span className="text-gray-600"> / </span>
                            <span className="text-gray-400">{maxUsers > 0 ? maxUsers : '∞'}</span>
                        </div>
                        {maxUsers > 0 && (
                            <div className="w-16 h-1 bg-gray-800 rounded-full overflow-hidden">
                                <div className={`h-full ${percent > 90 ? 'bg-red-500' : 'bg-neon-blue'}`} style={{ width: `${Math.min(percent, 100)}%` }}></div>
                            </div>
                        )}
                    </div>
                </td>
                <td className="px-6 py-4 text-sm">
                    {lic.expires_at ? (
                        <div className={`flex items-center gap-2 ${daysLeft <= 0 ? 'text-red-500' : daysLeft < 7 ? 'text-yellow-500' : 'text-green-500'}`}>
                            <Clock size={14} />
                            <span>{daysLeft > 0 ? `${daysLeft} días` : 'Expirada'}</span>
                        </div>
                    ) : <span className="text-gray-500 text-xs">Ilimitada</span>}
                </td>
                <td className="px-6 py-4">
                    <Badge status={lic.status} />
                </td>
                <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                        <button className="h-8 w-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/20 transition-colors" onClick={() => setExtendingLicense(lic)} title="Extender Validez">
                            <Calendar size={14} className="text-green-400" />
                        </button>
                        <button className="h-8 w-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/20 transition-colors" onClick={() => handleSimulateTraffic(lic)} title="Simular Tráfico">
                            <Beaker size={14} className="text-purple-400" />
                        </button>
                        <button className="h-8 w-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/20 transition-colors" onClick={() => handleDiagnose(lic)} title="Diagnóstico de Conexión">
                            <Activity size={14} className="text-yellow-400" />
                        </button>
                        <button className="h-8 w-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/20 transition-colors" onClick={() => openEdit(lic)} title="Configurar Features">
                            <Settings size={14} className="text-neon-blue" />
                        </button>
                        <button
                            onClick={() => setExpandedId(expandedId === lic.id ? null : lic.id)}
                            className={`h-8 w-8 flex items-center justify-center rounded-full transition-colors ${expandedId === lic.id ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white'}`}
                        >
                            {expandedId === lic.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                    </div>
                </td>
            </tr>
            {expandedId === lic.id && (
                <tr>
                    <td colSpan={6} className="bg-black/30 px-6 py-4 border-b border-white/5 border-l-2 border-l-neon-blue">
                        <ExpandedDetails lic={lic} revokeLicense={revokeLicense} restoreLicense={restoreLicense} isMobile={false} />
                    </td>
                </tr>
            )}
        </React.Fragment>
    )
}

function MobileLicenseCard({ lic, copyToClipboard, setExtendingLicense, openEdit, expandedId, setExpandedId, revokeLicense, restoreLicense, handleSimulateTraffic, handleDiagnose }) {
    const maxUsers = lic.max_users || lic.quotas?.max_users || 0
    const activeUsers = lic.active_users || 0
    const percent = maxUsers > 0 ? (activeUsers / maxUsers) * 100 : 0
    const daysLeft = lic.expires_at ? Math.ceil((new Date(lic.expires_at) - new Date()) / (1000 * 60 * 60 * 24)) : null

    return (
        <Card className="border border-white/10 bg-white/5 p-5">
            {/* Header Vertical */}
            <div className="flex flex-col gap-2 mb-4">
                <div className="flex justify-between items-start">
                    <h3 className="font-bold text-white text-xl">{lic.client_name}</h3>
                    <Badge status={lic.status} />
                </div>
                <div className="text-sm text-gray-400">
                    {lic.project_name || lic.project_rel_name || lic.projects?.name}
                </div>
                <button onClick={() => copyToClipboard(lic.client_id)} className="text-xs text-gray-500 font-mono flex items-center gap-1 self-start bg-white/5 px-2 py-1 rounded">
                    <Shield size={10} /> ID: {lic.client_id?.substring(0, 12)}... <Copy size={10} />
                </button>
            </div>

            {/* Progreso Vertical */}
            <div className="mb-6 bg-black/20 p-3 rounded-lg border border-white/5">
                <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-400">Usuarios Activos</span>
                    <span className="text-white font-mono">{activeUsers} / {maxUsers > 0 ? maxUsers : '∞'}</span>
                </div>
                {maxUsers > 0 && (
                    <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div className={`h-full transition-all duration-500 ${percent > 90 ? 'bg-red-500' : 'bg-neon-blue'}`} style={{ width: `${Math.min(percent, 100)}%` }}></div>
                    </div>
                )}
            </div>

            {/* Info Grid & Actions */}
            <div className="flex items-center justify-between border-t border-white/10 pt-4">
                <div className={`flex items-center gap-2 ${daysLeft === null ? 'text-gray-400' : daysLeft <= 0 ? 'text-red-500' : daysLeft < 7 ? 'text-yellow-500' : daysLeft < 30 ? 'text-orange-400' : 'text-green-500'}`}>
                    <Clock size={16} />
                    <span className="font-bold">{daysLeft === null ? 'Sin Límite' : daysLeft > 0 ? `${daysLeft} días` : 'Expirada'}</span>
                </div>

                <div className="flex gap-3">
                    <button className="h-10 w-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-green-500 hover:text-black transition-colors" onClick={() => setExtendingLicense(lic)} title="Extender Validez">
                        <Calendar size={20} />
                    </button>
                    <button className="h-10 w-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-purple-500 hover:text-black transition-colors" onClick={() => handleSimulateTraffic(lic)} title="Simular Tráfico">
                        <Beaker size={20} />
                    </button>
                    <button className="h-10 w-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-yellow-500 hover:text-black transition-colors" onClick={() => handleDiagnose(lic)} title="Diagnóstico">
                        <Activity size={20} />
                    </button>
                    <button className="h-10 w-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-neon-blue hover:text-black transition-colors" onClick={() => openEdit(lic)}>
                        <Settings size={20} />
                    </button>
                    <button
                        onClick={() => setExpandedId(expandedId === lic.id ? null : lic.id)}
                        className={`h-10 w-10 flex items-center justify-center rounded-full transition-colors ${expandedId === lic.id ? 'bg-neon-blue text-black' : 'bg-white/10 text-white'}`}
                    >
                        {expandedId === lic.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                </div>
            </div>

            {expandedId === lic.id && (
                <div className="mt-4 pt-4 border-t border-white/10 animate-in slide-in-from-top-2">
                    <ExpandedDetails lic={lic} revokeLicense={revokeLicense} restoreLicense={restoreLicense} isMobile={true} />
                </div>
            )}
        </Card>
    )
}

function ExpandedDetails({ lic, revokeLicense, restoreLicense, isMobile }) {
    return (
        <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'grid-cols-1 md:grid-cols-3 gap-6'}`}>
            <div>
                <h4 className="text-xs font-bold text-gray-500 uppercase mb-2 flex items-center gap-2">
                    <Zap size={12} /> Features
                </h4>
                <div className="flex flex-wrap gap-2">
                    {Object.entries(lic.features || {}).map(([key, val]) => (
                        <span key={key} className={`px-2 py-1 rounded text-[10px] font-mono border ${val ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20 opacity-50'}`}>
                            {key}: {val ? 'ON' : 'OFF'}
                        </span>
                    ))}
                </div>
            </div>

            <div className="flex items-end justify-end">
                {lic.status === 'revoked' ? (
                    <Button onClick={() => restoreLicense(lic.id)} variant="success" className="w-full">
                        <Unlock size={14} /> Restaurar
                    </Button>
                ) : (
                    <Button onClick={() => revokeLicense(lic.id, lic.client_name)} variant="danger" className="w-full bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20">
                        <Lock size={14} /> Revocar
                    </Button>
                )}
            </div>
        </div>
    )
}

function ModalOverlay({ title, children, onClose }) {
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
            {/* Contenedor del Modal con límites de altura y scroll */}
            <div className="w-full max-w-lg glass-panel rounded-xl flex flex-col max-h-[90vh] shadow-2xl bg-obsidian border border-white/10 relative">

                {/* Header Fijo */}
                <div className="p-6 border-b border-white/10 flex justify-between items-center shrink-0">
                    <h2 className="text-xl font-bold text-white">{title}</h2>
                    <button type="button" onClick={onClose} className="p-2 -mr-2 text-gray-400 hover:text-white rounded-full hover:bg-white/10 transition-colors">
                        ✕
                    </button>
                </div>

                {/* Contenido con Scroll */}
                <div className="p-6 overflow-y-auto custom-scrollbar">
                    {children}
                </div>
            </div>
        </div>
    )
}

function FeatureToggle({ label, active, onClick, danger }) {
    return (
        <div className="flex items-center justify-between p-2 rounded hover:bg-white/5 transition-colors cursor-pointer select-none" onClick={onClick}>
            <span className={`text-sm ${danger ? 'text-red-400' : 'text-gray-300'}`}>{label}</span>
            <div className={`w-10 h-5 rounded-full relative transition-colors ${active ? (danger ? 'bg-red-500' : 'bg-neon-green') : 'bg-gray-700'}`}>
                <div className={`absolute top-1 left-1 w-3 h-3 bg-white rounded-full transition-transform ${active ? 'translate-x-5' : 'translate-x-0'}`} />
            </div>
        </div>
    )
}
